import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Table, TablePaginationConfig, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { FilterValue } from "antd/es/table/interface";

import {
  CmtSyncRunAuthor,
  CmtSyncRunDuration,
  CmtSyncRunStartedAt,
  CmtSyncRunStatusTag,
  CmtSyncTriggerTag,
} from "@app/components/settings/CmtSyncRunDetails.tsx";
import { syncActionMessages } from "@app/components/settings/syncActionMessages.ts";
import {
  DEFAULT_PAGE,
  DEFAULT_SIZE,
} from "@app/constants/paginationConstants.ts";
import { SYNC_ACTIONS, SyncAction, SyncRun } from "@app/models/SyncAction.ts";
import { useSyncRuns } from "@app/services/hook/useSyncRuns.tsx";
import { colors } from "@app/theme.ts";

export const CmtSyncRunsTable = () => {
  const intl = useIntl();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [action, setAction] = useState<SyncAction | undefined>(undefined);
  const { syncRuns, isLoading } = useSyncRuns(page, size, action);

  const columns: ColumnsType<SyncRun> = [
    {
      title: (
        <FormattedMessage
          id="settings.history.action"
          defaultMessage="Action"
        />
      ),
      dataIndex: "action",
      key: "action",
      filters: SYNC_ACTIONS.map((syncAction) => ({
        text: intl.formatMessage(syncActionMessages[syncAction].label),
        value: syncAction,
      })),
      filterMultiple: false,
      filteredValue: action ? [action] : null,
      render: (syncAction: SyncAction) => (
        <FormattedMessage {...syncActionMessages[syncAction].label} />
      ),
    },
    {
      title: (
        <FormattedMessage
          id="settings.history.trigger"
          defaultMessage="Trigger"
        />
      ),
      dataIndex: "trigger",
      key: "trigger",
      render: (_, run) => <CmtSyncTriggerTag trigger={run.trigger} />,
    },
    {
      title: (
        <FormattedMessage id="settings.history.who" defaultMessage="Who" />
      ),
      key: "who",
      render: (_, run) => <CmtSyncRunAuthor run={run} />,
    },
    {
      title: (
        <FormattedMessage
          id="settings.history.started_at"
          defaultMessage="Started at"
        />
      ),
      dataIndex: "startedAt",
      key: "startedAt",
      render: (_, run) => <CmtSyncRunStartedAt run={run} />,
    },
    {
      title: (
        <FormattedMessage
          id="settings.history.duration"
          defaultMessage="Duration"
        />
      ),
      key: "duration",
      render: (_, run) => <CmtSyncRunDuration run={run} />,
    },
    {
      title: (
        <FormattedMessage
          id="settings.history.status"
          defaultMessage="Status"
        />
      ),
      dataIndex: "status",
      key: "status",
      render: (_, run) => <CmtSyncRunStatusTag status={run.status} />,
    },
  ];

  const onChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
  ) => {
    const selectedAction = filters.action?.[0] as SyncAction | undefined;
    if (selectedAction !== action) {
      setAction(selectedAction);
      setPage(DEFAULT_PAGE);
      return;
    }
    setPage(pagination.current ?? DEFAULT_PAGE);
    setSize(pagination.pageSize ?? DEFAULT_SIZE);
  };

  return (
    <Table<SyncRun>
      style={{
        overflow: "hidden",
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        background: colors.white,
      }}
      rowKey="id"
      columns={columns}
      dataSource={syncRuns?.content}
      loading={isLoading}
      onChange={onChange}
      pagination={{
        current: page,
        pageSize: size,
        total: syncRuns?.totalElements,
      }}
      expandable={{
        rowExpandable: (run) => run.status === "FAILURE" && !!run.errorMessage,
        expandedRowRender: (run) => (
          <Typography.Text type="danger" style={{ whiteSpace: "pre-wrap" }}>
            {run.errorMessage}
          </Typography.Text>
        ),
      }}
    />
  );
};
